/**
 * @voltxampere/web — Lit ↔ Preact Signals bridge (Sprint 11).
 *
 * `SignalController` adapts a Preact Signal to Lit's reactive
 * controller protocol: when the signal mutates, the host
 * element's `requestUpdate()` is called, which schedules a
 * re-render. Supports multiple signals via repeated controller
 * registration.
 *
 * Lifecycle:
 *   - hostConnected → subscribe (open `effect`)
 *   - hostDisconnected → dispose effect
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { ReactiveController, ReactiveControllerHost } from "lit";
import type { Signal } from "@preact/signals-core";
import { effect } from "@preact/signals-core";

export class SignalController<T> implements ReactiveController {
  private dispose: (() => void) | undefined;

  constructor(
    private readonly host: ReactiveControllerHost,
    private readonly source: Signal<T>,
  ) {
    host.addController(this);
  }

  hostConnected(): void {
    let first = true;
    this.dispose = effect(() => {
      // Reading the signal value subscribes the effect to it.
      // The const + `void` discard satisfies the project's
      // no-unused-expressions rule without disabling tracking.
      const _track = this.source.value;
      void _track;
      // Skip the synchronous initial run — Lit already renders
      // once on connect; an extra requestUpdate is harmless but
      // wasteful, especially in tests.
      if (first) {
        first = false;
        return;
      }
      this.host.requestUpdate();
    });
  }

  hostDisconnected(): void {
    this.dispose?.();
    this.dispose = undefined;
  }

  /** Read the current value from inside `render()`. */
  get value(): T {
    return this.source.value;
  }

  /** Mutate the underlying signal. */
  set value(next: T) {
    this.source.value = next;
  }
}
